import { fotoDeHerramienta } from '@/lib/supabase/deposito'
import type { Herramienta } from '@/lib/supabase/tipos-bd'

import type { Eleccion } from './useHerramientas'
import estilos from './HerramientasDeLaOrden.module.css'

/**
 * Qué herramientas se van con la orden.
 *
 * VA DENTRO DE PROGRAMAR Y NO EN UNA PANTALLA APARTE porque es cuando se
 * decide: quien está diciendo «Eduard, el jueves, ocho horas» es la misma
 * persona, en el mismo momento, que sabe que hace falta la escalera de 16. Un
 * paso separado se olvida, y una herramienta que no se apuntó es una que
 * mañana nadie encuentra.
 *
 * SE MARCA, NO SE ESCRIBE. Son catorce; una lista con casillas se recorre de
 * un vistazo. El contador de cantidad solo aparece donde hay más de una
 * unidad, que son siete de las catorce: ponerle un «1» fijo al lado a la
 * escalera de 32 —de la que hay una sola— es un número que no decide nada.
 */

type Props = {
  herramientas: Herramienta[]
  /** Lo que la orden ya tenía apartado, para no contárselo como ocupado. */
  yaTiene: Eleccion
  /** 0318: Lo que el técnico sugirió en la cotización. */
  sugeridas?: Eleccion
  eleccion: Eleccion
  onCambiar: (eleccion: Eleccion) => void
}

export function HerramientasDeLaOrden({
  herramientas,
  yaTiene,
  sugeridas = {},
  eleccion,
  onCambiar,
}: Props) {
  /**
   * Cuántas puede llevarse ESTA orden.
   *
   * A lo que hay libre se le suma lo que ella misma ya tenía apartado: si no,
   * volver a abrir la programación de una orden que ya se llevó la única
   * escalera de 32 diría «no queda ninguna» y no dejaría ni confirmar lo que
   * ya estaba decidido.
   */
  const alcanceDe = (h: Herramienta) => h.cantidad_disponible + (yaTiene[h.id] ?? 0)

  function alternar(h: Herramienta) {
    const siguiente = { ...eleccion }
    if (siguiente[h.id]) delete siguiente[h.id]
    else siguiente[h.id] = 1
    onCambiar(siguiente)
  }

  function cambiarCantidad(h: Herramienta, cuantas: number) {
    const tope = alcanceDe(h)
    onCambiar({ ...eleccion, [h.id]: Math.max(1, Math.min(cuantas, tope)) })
  }

  const elegidas = Object.values(eleccion).filter((c) => c > 0).length
  const haySugeridas = Object.keys(sugeridas).length > 0

  return (
    <section className={estilos.zona}>
      <header className={estilos.cabecera}>
        <div>
          <span className={estilos.titulo}>Herramienta que se lleva</span>
          {haySugeridas && (
            <span className={estilos.sugeridaAviso}>
              · Sugeridas por el técnico al cotizar
            </span>
          )}
        </div>
        <span className={estilos.cuenta}>
          {elegidas === 0 ? 'Ninguna' : `${elegidas} de ${herramientas.length}`}
        </span>
      </header>

      <ul className={estilos.lista}>
        {herramientas.map((h) => {
          const tope = alcanceDe(h)
          const marcada = (eleccion[h.id] ?? 0) > 0
          const sugeridaPorTecnico = (sugeridas[h.id] ?? 0) > 0
          // Sin unidades libres no se puede marcar. Pero se sigue viendo:
          // saber que la escalera de 32 existe y está ocupada es información,
          // y esconderla haría creer que MEGA no tiene ninguna.
          const agotada = tope === 0

          return (
            <li key={h.id} className={agotada ? estilos.filaAgotada : estilos.fila}>
              <label className={estilos.marca}>
                <input
                  type="checkbox"
                  checked={marcada}
                  disabled={agotada}
                  onChange={() => alternar(h)}
                />

                {/* Aquí la foto gana lo suyo: quien programa tiene que
                    distinguir la escalera de 28 pasos de la de 32 sin
                    tenerlas delante, y los nombres se parecen demasiado. */}
                {fotoDeHerramienta(h.foto) && (
                  <img className={estilos.foto} src={fotoDeHerramienta(h.foto)!} alt="" loading="lazy" />
                )}

                <span className={estilos.nombre}>{h.nombre}</span>
                <span className={estilos.codigo}>{h.codigo}</span>
                {sugeridaPorTecnico && (
                  <span className={estilos.etiquetaSugerida}>Sugerida</span>
                )}
              </label>

              <div className={estilos.derecha}>
                {agotada ? (
                  <span className={estilos.ocupada}>Toda en la calle</span>
                ) : (
                  <span className={estilos.quedan}>
                    {tope} de {h.cantidad_total}
                  </span>
                )}

                {/* Solo donde hay de qué elegir. */}
                {marcada && tope > 1 && (
                  <span className={estilos.contador}>
                    <button
                      type="button"
                      onClick={() => cambiarCantidad(h, (eleccion[h.id] ?? 1) - 1)}
                      disabled={(eleccion[h.id] ?? 1) <= 1}
                      aria-label={`Una menos de ${h.nombre}`}
                    >
                      −
                    </button>
                    <b>{eleccion[h.id] ?? 1}</b>
                    <button
                      type="button"
                      onClick={() => cambiarCantidad(h, (eleccion[h.id] ?? 1) + 1)}
                      disabled={(eleccion[h.id] ?? 1) >= tope}
                      aria-label={`Una más de ${h.nombre}`}
                    >
                      +
                    </button>
                  </span>
                )}
              </div>
            </li>
          )
        })}
      </ul>

      <p className={estilos.pie}>
        Sale de bodega al programar y no vuelve a estar disponible hasta que alguien la reciba.
      </p>
    </section>
  )
}
